/**
 * The packed form of a region map (colour + height per block column, which chunks exist): what "Share world"
 * uploads for guests without a jar, and what the website's demo world is made of (tools/make-demo-pack.ts).
 * Plain Web APIs only, so it also runs in Node.
 *
 *   "MCPM" · version 1 · 3 bytes reserved · presence[1024] · height int16 LE [512×512] · colour RGBA [512×512], gzip
 */
import { inflate } from "@mcp/world";
import type { RegionMapResult } from "../engine/protocol.ts";

const MAP_MAGIC = [0x4d, 0x43, 0x50, 0x4d]; // "MCPM"

async function gzip(bytes: Uint8Array) {
  const s = new Blob([bytes as BlobPart]).stream().pipeThrough(new CompressionStream("gzip"));
  return new Uint8Array(await new Response(s).arrayBuffer());
}

export async function packMap(r: RegionMapResult): Promise<Uint8Array> {
  const n = 512 * 512;
  const buf = new Uint8Array(8 + 1024 + n * 2 + n * 4);
  buf.set(MAP_MAGIC, 0);
  buf[4] = 1;
  buf.set(r.presence, 8);
  const hv = new DataView(buf.buffer, 8 + 1024, n * 2);
  for (let i = 0; i < n; i++) hv.setInt16(i * 2, r.height[i], true);
  buf.set(r.color, 8 + 1024 + n * 2);
  return gzip(buf);
}

export async function unpackMap(rx: number, rz: number, gz: Uint8Array): Promise<RegionMapResult> {
  const buf = await inflate(gz, "gzip");
  if (buf[0] !== MAP_MAGIC[0] || buf[1] !== MAP_MAGIC[1] || buf[4] !== 1) throw new Error("bad map blob");
  const n = 512 * 512;
  const presence = buf.slice(8, 8 + 1024);
  const height = new Int16Array(n);
  const hv = new DataView(buf.buffer, buf.byteOffset + 8 + 1024, n * 2);
  for (let i = 0; i < n; i++) height[i] = hv.getInt16(i * 2, true);
  const color = buf.slice(8 + 1024 + n * 2, 8 + 1024 + n * 6);
  return { rx, rz, color, height, presence };
}
